import { unref } from 'vue'
import { useAppStore } from '@/stores/app'
import { usePaidFeatureStore } from '@/stores/paidFeature'
import { hasPaidFeatureAccess } from '@/utils/paidFeatureAccess'

/**
 * Vue directive: v-require-paid
 *
 * Use on clickable elements that trigger a plan-gated feature. Pass the feature key (e.g. "announcements", "mailing_list").
 * On click:
 * - If the user's plan includes the feature (user.hasFeature(featureKey) or plan_slug grants it): the click proceeds normally.
 * - Otherwise: default is prevented, propagation is stopped, and the upgrade modal is shown (Growth or Pro as needed).
 *
 * Usage:
 *   <button v-require-paid="'announcements'" @click="openAnnouncement">Announcements</button>
 *   <button v-require-paid="'mailing_list'">Mailing list</button>
 */

export function requirePaidDirective(app) {
  app.directive('require-paid', {
    mounted(el, binding) {
      el._requirePaidClick = (e) => {
        const appStore = useAppStore()
        const user = unref(appStore.user)
        const featureKey = binding.value ?? undefined
        const key = typeof featureKey === 'string' ? featureKey : undefined
        const hasAccess = key ? hasPaidFeatureAccess(user, key) : (user?.isPaid === true)
        if (!hasAccess) {
          e.preventDefault()
          e.stopPropagation()
          const paidStore = usePaidFeatureStore()
          paidStore.open(key ? { featureKey: key } : {})
        }
      }
      el.addEventListener('click', el._requirePaidClick, true)
    },
    beforeUnmount(el) {
      if (el._requirePaidClick) {
        el.removeEventListener('click', el._requirePaidClick, true)
      }
    },
  })
}
